import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { Search } from 'lucide-react';
import { Star } from 'lucide-react';
import { Download } from 'lucide-react';
import { Settings } from 'lucide-react';
import { ExternalLink } from 'lucide-react';
import { Zap } from 'lucide-react';
import { RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@edusense/shared-ui-kit';
import { Tooltip } from '@heroui/react';
import { mcpMarketplaceAPI } from '../../services/mcpMarketplaceApi';
import { MarketplaceWrapper, Header, SearchSection, ServerGrid, ServerCard, ServerInfo, ServerActions, CategoryTabs, EmptyState } from './MCPMarketplace.styles';
const MCPMarketplace = ({ onInstallServer, onUninstallServer, installedServers })=>{
    const [servers, setServers] = useState([]);
    const [filteredServers, setFilteredServers] = useState([]);
    const [categories, setCategories] = useState([
        {
            id: 'all',
            name: '전체',
            count: 0
        }
    ]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('rating');
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    // 초기 데이터 로드
    useEffect(()=>{
        loadMarketplaceData();
    }, []);
    // 서버 검색 및 필터링
    useEffect(()=>{
        if (!isInitialLoading) {
            searchServers();
        }
    }, [
        selectedCategory,
        searchQuery,
        sortBy,
        isInitialLoading
    ]);
    const loadMarketplaceData = async ()=>{
        setIsInitialLoading(true);
        try {
            // 카테고리 로드
            const categoryData = await mcpMarketplaceAPI.getCategories();
            setCategories([
                {
                    id: 'all',
                    name: '전체',
                    count: categoryData.reduce((sum, cat)=>sum + cat.count, 0)
                },
                ...categoryData
            ]);
            // 초기 서버 로드
            await searchServers();
        } catch (error) {
            console.error('Failed to load marketplace data:', error);
        } finally{
            setIsInitialLoading(false);
        }
    };
    const searchServers = async ()=>{
        setIsLoading(true);
        try {
            const searchParams = {
                query: searchQuery || undefined,
                category: selectedCategory !== 'all' ? selectedCategory : undefined,
                sort_by: sortBy,
                limit: 50
            };
            const serverData = await mcpMarketplaceAPI.searchServers(searchParams);
            // API 데이터를 UI 형식으로 변환
            const formattedServers = serverData.map((server)=>({
                    ...server,
                    id: `${server.publisher_id}-${server.content_name}`.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
                    name: server.content_name,
                    version: server.version || '1.0.0',
                    author: server.publisher_id.replace('pub-', ''),
                    rating: parseFloat(server.rating) || 0,
                    installCount: server.install_count || parseInt(server.review_cnt) * 10 || 0,
                    endpoint: server.endpoint || '',
                    transport: server.transport_type || 'sse',
                    authType: server.auth_type || 'none',
                    isInstalled: installedServers.includes(`${server.publisher_id}-${server.content_name}`.toLowerCase().replace(/[^a-z0-9-]/g, '-')),
                    repositoryUrl: server.website,
                    homepage: server.website
                }));
            setServers(formattedServers);
            setFilteredServers(formattedServers);
        } catch (error) {
            console.error('Failed to search servers:', error);
            // Fallback to local data
            const fallbackServers = [
                {
                    id: 'edusense-sse',
                    name: 'Edusense SSE MCP',
                    description: 'Edusense 통합 SSE MCP 서버 (DCR OAuth2 지원)',
                    version: '1.2.0',
                    author: 'Edusense Team',
                    category: 'api-integration',
                    tags: [
                        'education',
                        'sse',
                        'oauth2',
                        'real-time'
                    ],
                    capabilities: [
                        'edusense',
                        'sse',
                        'real-time',
                        'notifications',
                        'dcr-oauth2'
                    ],
                    rating: 4.8,
                    installCount: 1247,
                    endpoint: 'https://edusense.ugot.uk/sse',
                    transport: 'sse',
                    authType: 'oauth2',
                    isInstalled: true,
                    homepage: 'https://edusense.ugot.uk'
                }
            ];
            setServers(fallbackServers);
            setFilteredServers(fallbackServers);
        } finally{
            setIsLoading(false);
        }
    };
    const handleInstall = async (server)=>{
        setIsLoading(true);
        try {
            await onInstallServer(server);
            setServers((prev)=>prev.map((s)=>s.id === server.id ? {
                        ...s,
                        isInstalled: true,
                        installCount: s.installCount + 1
                    } : s));
            setFilteredServers((prev)=>prev.map((s)=>s.id === server.id ? {
                        ...s,
                        isInstalled: true,
                        installCount: s.installCount + 1
                    } : s));
        } catch (error) {
            console.error('서버 설치 실패:', error);
        } finally{
            setIsLoading(false);
        }
    };
    const handleUninstall = async (serverId)=>{
        setIsLoading(true);
        try {
            await onUninstallServer(serverId);
            setServers((prev)=>prev.map((s)=>s.id === serverId ? {
                        ...s,
                        isInstalled: false
                    } : s));
            setFilteredServers((prev)=>prev.map((s)=>s.id === serverId ? {
                        ...s,
                        isInstalled: false
                    } : s));
        } catch (error) {
            console.error('서버 제거 실패:', error);
        } finally{
            setIsLoading(false);
        }
    };
    const handleRefresh = ()=>{
        loadMarketplaceData();
    };
    const renderTransportIcon = (transport)=>{
        switch(transport){
            case 'sse':
                return /*#__PURE__*/ _jsx(Zap, {
                    className: "w-4 h-4 text-blue-500"
                });
            case 'websocket':
                return /*#__PURE__*/ _jsx(Zap, {
                    className: "w-4 h-4 text-green-500"
                });
            case 'stdio':
                return /*#__PURE__*/ _jsx(Zap, {
                    className: "w-4 h-4 text-purple-500"
                });
            default:
                return /*#__PURE__*/ _jsx(Zap, {
                    className: "w-4 h-4 text-gray-500"
                });
        }
    };
    return /*#__PURE__*/ _jsxs(MarketplaceWrapper, {
        children: [
            /*#__PURE__*/ _jsxs(Header, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h1", {
                                children: "MCP Marketplace"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: "실시간 remote SSE 서버를 포함한 MCP 서버를 발견하고 설치하세요"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        iconOnly: true,
                        variant: "ghost",
                        size: "sm",
                        icon: /*#__PURE__*/ _jsx(RefreshCw, {
                            className: `w-4 h-4 ${isInitialLoading || isLoading ? 'animate-spin' : ''}`
                        }),
                        onClick: handleRefresh,
                        disabled: isInitialLoading || isLoading,
                        label: "새로고침"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SearchSection, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "search-container",
                        children: [
                            /*#__PURE__*/ _jsx(Search, {
                                className: "search-icon"
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                type: "text",
                                placeholder: "MCP 서버 검색...",
                                value: searchQuery,
                                onChange: (e)=>setSearchQuery(e.target.value)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "filter-controls",
                        children: /*#__PURE__*/ _jsxs("select", {
                            value: sortBy,
                            onChange: (e)=>setSortBy(e.target.value),
                            children: [
                                /*#__PURE__*/ _jsx("option", {
                                    value: "rating",
                                    children: "평점순"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "installs",
                                    children: "설치순"
                                }),
                                /*#__PURE__*/ _jsx("option", {
                                    value: "name",
                                    children: "이름순"
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CategoryTabs, {
                children: categories.map((category)=>/*#__PURE__*/ _jsxs("button", {
                        className: selectedCategory === category.id ? 'active' : '',
                        onClick: ()=>setSelectedCategory(category.id),
                        disabled: isInitialLoading,
                        children: [
                            category.name,
                            /*#__PURE__*/ _jsx("span", {
                                className: "count",
                                children: category.count
                            })
                        ]
                    }, category.id))
            }),
            /*#__PURE__*/ _jsx(ServerGrid, {
                children: /*#__PURE__*/ _jsx(AnimatePresence, {
                    children: isInitialLoading ? /*#__PURE__*/ _jsxs(EmptyState, {
                        children: [
                            /*#__PURE__*/ _jsx(RefreshCw, {
                                size: 48,
                                className: "animate-spin"
                            }),
                            /*#__PURE__*/ _jsx("h3", {
                                children: "MCP 마켓플레이스 로딩 중..."
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: "remote SSE 서버들을 가져오고 있습니다"
                            })
                        ]
                    }) : filteredServers.length === 0 ? /*#__PURE__*/ _jsxs(EmptyState, {
                        children: [
                            /*#__PURE__*/ _jsx(Search, {
                                size: 48
                            }),
                            /*#__PURE__*/ _jsx("h3", {
                                children: "검색 결과가 없습니다"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                children: "다른 검색어나 카테고리를 시도해보세요"
                            })
                        ]
                    }) : filteredServers.map((server)=>/*#__PURE__*/ _jsxs(ServerCard, {
                            as: motion.div,
                            initial: {
                                opacity: 0,
                                y: 20
                            },
                            animate: {
                                opacity: 1,
                                y: 0
                            },
                            exit: {
                                opacity: 0,
                                y: -20
                            },
                            whileHover: {
                                scale: 1.02
                            },
                            children: [
                                /*#__PURE__*/ _jsxs(ServerInfo, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "server-header",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "server-title",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("h3", {
                                                            children: server.name
                                                        }),
                                                        renderTransportIcon(server.transport)
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "server-meta",
                                                    children: [
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: "version",
                                                            children: [
                                                                "v",
                                                                server.version
                                                            ]
                                                        }),
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: "author",
                                                            children: [
                                                                "by ",
                                                                server.author
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "description",
                                            children: server.description
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "capabilities",
                                            children: [
                                                server.capabilities.slice(0, 4).map((capability)=>/*#__PURE__*/ _jsx("span", {
                                                        className: "capability",
                                                        children: capability
                                                    }, capability)),
                                                server.capabilities.length > 4 && /*#__PURE__*/ _jsxs("span", {
                                                    className: "capability more",
                                                    children: [
                                                        "+",
                                                        server.capabilities.length - 4
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "stats",
                                            children: [
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "stat",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Star, {
                                                            className: "w-4 h-4 text-yellow-400"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            children: server.rating
                                                        })
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "stat",
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Download, {
                                                            className: "w-4 h-4 text-gray-500"
                                                        }),
                                                        /*#__PURE__*/ _jsx("span", {
                                                            children: server.installCount.toLocaleString()
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(ServerActions, {
                                    children: [
                                        server.isInstalled ? /*#__PURE__*/ _jsxs("div", {
                                            className: "installed-actions",
                                            children: [
                                                /*#__PURE__*/ _jsx(Button, {
                                                    variant: "secondary",
                                                    size: "sm",
                                                    leftIcon: /*#__PURE__*/ _jsx(Settings, {
                                                        className: "w-4 h-4"
                                                    }),
                                                    onClick: ()=>{},
                                                    children: "설정"
                                                }),
                                                /*#__PURE__*/ _jsx(Button, {
                                                    variant: "danger",
                                                    size: "sm",
                                                    onClick: ()=>handleUninstall(server.id),
                                                    disabled: isLoading,
                                                    children: "제거"
                                                })
                                            ]
                                        }) : /*#__PURE__*/ _jsx(Button, {
                                            variant: "primary",
                                            size: "sm",
                                            leftIcon: /*#__PURE__*/ _jsx(Download, {
                                                className: "w-4 h-4"
                                            }),
                                            onClick: ()=>handleInstall(server),
                                            disabled: isLoading,
                                            isLoading: isLoading,
                                            children: "설치"
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "external-links",
                                            children: [
                                                server.repositoryUrl && /*#__PURE__*/ _jsx(Tooltip, {
                                                    content: "GitHub 저장소",
                                                    children: /*#__PURE__*/ _jsx(Button, {
                                                        iconOnly: true,
                                                        variant: "ghost",
                                                        size: "sm",
                                                        icon: /*#__PURE__*/ _jsx(ExternalLink, {
                                                            className: "w-4 h-4"
                                                        }),
                                                        onClick: ()=>window.open(server.repositoryUrl, '_blank'),
                                                        label: "저장소"
                                                    })
                                                }),
                                                server.homepage && /*#__PURE__*/ _jsx(Tooltip, {
                                                    content: "홈페이지",
                                                    children: /*#__PURE__*/ _jsx(Button, {
                                                        iconOnly: true,
                                                        variant: "ghost",
                                                        size: "sm",
                                                        icon: /*#__PURE__*/ _jsx(ExternalLink, {
                                                            className: "w-4 h-4"
                                                        }),
                                                        onClick: ()=>window.open(server.homepage, '_blank'),
                                                        label: "홈페이지"
                                                    })
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }, server.id))
                })
            })
        ]
    });
};
export default MCPMarketplace;
